import { createFileRoute } from '@tanstack/react-router'
import { noop } from '@tanstack/react-query'
import { trpc } from '@/main'
import { DescribeJobDialogPending } from '@/routes/_app/-components/shared/dialog-pending'
import { idParams, useModalSlot } from '@/components/custom/modal-slot'
import DescribeJobDialog from '@/routes/_app/-dialogs/describe-job'
import type { Indicator } from '@/routes/_app/-components/offers/describe-dialog-pending'

const jobIdParams = idParams('jobId')
const INDICATORS: readonly string[] = ['competences', 'values', 'personality'] satisfies Indicator[]

function parseIndicator(value: string | undefined): Indicator | undefined {
  if (value === undefined) return undefined
  if (!INDICATORS.includes(value)) throw new Error(`not an indicator: ${value}`)
  return value as Indicator
}

export const Route = createFileRoute('/@modal/jobs/$jobId/describe/{-$indicator}')({
  params: {
    // `indicator` narrows the wizard to one step (the action menus' "Describe competencies" rows).
    parse: (raw: { jobId: string; indicator?: string }) => ({
      ...jobIdParams.parse(raw),
      indicator: parseIndicator(raw.indicator),
    }),
    stringify: (params: { jobId: string; indicator?: Indicator }) => ({
      ...jobIdParams.stringify(params),
      indicator: params.indicator,
    }),
  },
  staticData: { slotScope: '/_app' },
  loader: ({ params, context }) => {
    const jobId = Number(params.jobId)
    const types = params.indicator ? [params.indicator] : (INDICATORS as Indicator[])
    // query: direct the wizard keys its own reads; no route owns a dialog's key
    // prefetch: speculative warms the job line on intent, so a hovered open lands filled in
    context.queryClient.query(trpc.jobs.getJob.queryOptions({ jobId })).catch(noop)
    for (const type of types) {
      const options = trpc.companies.getOfferProfileDescription.queryOptions({ offerId: jobId, type })
      // query: direct as above, the selections the wizard seeds from
      // prefetch: speculative as above
      context.queryClient.query(options).catch(noop)
    }
  },
  pendingComponent: DescribeJobDialogPending,
  component: DescribeJobSlot,
})

function DescribeJobSlot() {
  const { params, ...slot } = useModalSlot(Route)
  return <DescribeJobDialog jobId={Number(params.jobId)} indicator={params.indicator} {...slot} />
}
